import { __assign, __awaiter, __generator } from "tslib";
import { Mark, mergeAttributes } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Constants } from './constants';
import { EditorService } from "./editor";
import { Plugin } from "./plugin";
import { VeltService } from "./velt";
var VELT_SDK_VERSION = '4.5.0-beta.13';
export var triggerAddComment = function (editor, config) { return __awaiter(void 0, void 0, void 0, function () {
    return __generator(this, function (_a) {
        try {
            return [2 /*return*/, addComment({ editor: editor, context: config === null || config === void 0 ? void 0 : config.context })];
        }
        catch (error) {
            VeltService.catchError('Error triggering add comment', error);
        }
        return [2 /*return*/];
    });
}); };
/**
 * Creates a comment annotation for the currently selected text in the editor
 * This function handles the entire flow from getting the selected text to creating
 * the actual comment through the Velt service
 */
export var addComment = function (_a) {
    var editorId = _a.editorId, editor = _a.editor, clientContext = _a.context;
    return __awaiter(void 0, void 0, void 0, function () {
        var commentElement, plugin, selectedText, targetTextNode, targetTextNodeId, locationId, currentSelectionFrom, currentSelectionTo, occurrence, context, location_1;
        var _b, _c, _d;
        return __generator(this, function (_e) {
            try {
                commentElement = VeltService.getCommentElement();
                if (!commentElement)
                    return [2 /*return*/];
                // Ensure we have an editor ID for proper tracking
                if (!editorId) {
                    editorId = (_b = EditorService.getEditorId(editor)) !== null && _b !== void 0 ? _b : undefined;
                }
                plugin = Plugin.getInstance({ editorId: editorId, editor: editor });
                selectedText = plugin.editorService.getSelectedText(editor);
                // Exit if no text is selected - nothing to comment on
                if (!selectedText) {
                    return [2 /*return*/];
                }
                targetTextNode = plugin.editorService.findParentNodeWithId(editor);
                targetTextNodeId = undefined;
                locationId = undefined;
                if (targetTextNode) {
                    targetTextNodeId = targetTextNode.id;
                    locationId = (_c = targetTextNode.closest("[".concat(Constants.ATTRIBUTES.LOCATION_ID, "]"))) === null || _c === void 0 ? void 0 : _c.getAttribute(Constants.ATTRIBUTES.LOCATION_ID);
                }
                currentSelectionFrom = editor.state.selection.from;
                currentSelectionTo = editor.state.selection.to;
                occurrence = plugin.editorService.findOccurrenceIndex(editor, selectedText, targetTextNode, currentSelectionFrom, currentSelectionTo);
                // Exit if we can't determine occurrence information
                if (!occurrence) {
                    return [2 /*return*/];
                }
                context = {};
                if (clientContext && typeof clientContext === 'object') {
                    context = __assign(__assign({}, context), clientContext);
                }
                context.textEditorConfig = {
                    text: selectedText,
                    occurrence: occurrence,
                    targetTextNodeId: targetTextNodeId,
                };
                if (editorId) {
                    context.textEditorConfig.editorId = editorId;
                }
                location_1 = undefined;
                if (locationId) {
                    location_1 = { id: locationId };
                }
                (_d = commentElement === null || commentElement === void 0 ? void 0 : commentElement.addManualComment({ context: context, location: location_1 })) === null || _d === void 0 ? void 0 : _d.then(function (data) {
                    var _a, _b;
                    if (!editor.storage.tiptapVeltComments.persistVeltMarks) {
                        var annotationId = (_a = data === null || data === void 0 ? void 0 : data.annotation) === null || _a === void 0 ? void 0 : _a.annotationId;
                        var multiThreadAnnotationId = (_b = data === null || data === void 0 ? void 0 : data.annotation) === null || _b === void 0 ? void 0 : _b.multiThreadAnnotationId;
                        editor.chain().focus().setVeltComment(annotationId, multiThreadAnnotationId).run();
                    }
                    // reset editor selection
                    editor.chain().focus().run();
                });
            }
            catch (error) {
                VeltService.catchError('Error adding tiptap-velt-comments', error);
            }
            return [2 /*return*/];
        });
    });
};
// Exposing old method for backwards compatibility
export var addTiptapVeltComment = triggerAddComment;
export var TiptapVeltComments = Mark.create({
    name: 'tiptapVeltComments',
    addOptions: function () {
        return {
            HTMLAttributes: {},
            persistVeltMarks: true,
            editorId: undefined,
        };
    },
    configure: function (config) {
        if (config === void 0) { config = {}; }
        return this.extend(__assign(__assign(__assign({}, this.options), config), { HTMLAttributes: __assign(__assign({}, this.options.HTMLAttributes), (config.HTMLAttributes || {})) }));
    },
    addStorage: function () {
        return {
            persistVeltMarks: this.options.persistVeltMarks,
            editorId: this.options.editorId,
        };
    },
    parseHTML: function () {
        return [
            {
                tag: 'velt-comment-text',
                getAttrs: function (node) {
                    var annotationId = node.getAttribute('annotation-id');
                    var multiThreadAnnotationId = node.getAttribute('multi-thread-annotation-id');
                    return {
                        'annotation-id': annotationId,
                        'multi-thread-annotation-id': multiThreadAnnotationId,
                    };
                },
            },
        ];
    },
    renderHTML: function (_a) {
        var HTMLAttributes = _a.HTMLAttributes, mark = _a.mark;
        return ['velt-comment-text', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
    },
    addAttributes: function () {
        return {
            'annotation-id': {
                default: null,
                parseHTML: function (element) { return element.getAttribute('annotation-id'); },
                renderHTML: function (attributes) {
                    if (!attributes['annotation-id']) {
                        return {};
                    }
                    return { 'annotation-id': attributes['annotation-id'] };
                },
            },
            'multi-thread-annotation-id': {
                default: null,
                parseHTML: function (element) { return element.getAttribute('multi-thread-annotation-id'); },
                renderHTML: function (attributes) {
                    if (!attributes['multi-thread-annotation-id']) {
                        return {};
                    }
                    return { 'multi-thread-annotation-id': attributes['multi-thread-annotation-id'] };
                },
            },
        };
    },
    addCommands: function () {
        var _this = this;
        return {
            setVeltComment: function (annotationId, multiThreadAnnotationId, from, to) {
                return function (_a) {
                    var tr = _a.tr, state = _a.state, dispatch = _a.dispatch;
                    var posFrom = from !== undefined ? from : state.selection.from;
                    var posTo = to !== undefined ? to : state.selection.to;
                    if (dispatch) {
                        var attrs = {};
                        if (multiThreadAnnotationId) {
                            attrs['multi-thread-annotation-id'] = multiThreadAnnotationId;
                        }
                        else if (annotationId) {
                            attrs['annotation-id'] = annotationId;
                        }
                        tr.addMark(posFrom, posTo, _this.type.create(attrs));
                        return true;
                    }
                    return false;
                };
            },
        };
    },
    onApply: function () {
        var _this = this;
        var _a;
        var editorId = this.editor.storage.tiptapVeltComments.editorId;
        if (!editorId && this.editor) {
            editorId = (_a = EditorService.getEditorId(this.editor)) !== null && _a !== void 0 ? _a : undefined;
        }
        var plugin = Plugin.getInstance({ editorId: editorId, editor: this.editor });
        this.editor.registerPlugin({
            key: new PluginKey('tiptapVeltCommentsPlugin'),
            props: {
                decorations: function (state) {
                    var decorations = [];
                    state.doc.descendants(function (node, pos) {
                        var mark = node.marks.find(function (m) { return m.type.name === _this.name; });
                        if (mark) {
                            var dom = document.createElement('velt-comment-text');
                            dom.setAttribute('annotation-id', mark.attrs['annotation-id']);
                            dom.setAttribute('multi-thread-annotation-id', mark.attrs['multi-thread-annotation-id']);
                            if (mark.attrs['annotation-id']) {
                                var existingAnnotation = plugin.storeService.annotationStore.get(mark.attrs['annotation-id']);
                                plugin.storeService.annotationStore.set(mark.attrs['annotation-id'], {
                                    annotation: (existingAnnotation === null || existingAnnotation === void 0 ? void 0 : existingAnnotation.annotation)
                                        ? __assign(__assign({}, existingAnnotation.annotation), { text: node.text || '' }) : {
                                        annotationId: mark.attrs['annotation-id'],
                                        multiThreadAnnotationId: mark.attrs['multi-thread-annotation-id'],
                                        text: node.text || '',
                                    },
                                    pluginReference: {
                                        node: dom,
                                        position: { from: pos, to: pos + node.nodeSize },
                                    },
                                });
                            }
                            decorations.push(Decoration.widget(pos, dom));
                        }
                    });
                    return DecorationSet.create(state.doc, decorations);
                },
            },
        });
    },
    // This hook runs on every transaction
    onTransaction: function (_a) {
        var _b;
        var transaction = _a.transaction, editor = _a.editor;
        try {
            if (!transaction.docChanged)
                return;
            if (!editor.storage.tiptapVeltComments.persistVeltMarks)
                return;
            var editorId = this.editor.storage.tiptapVeltComments.editorId;
            if (!editorId) {
                editorId = (_b = EditorService.getEditorId(editor)) !== null && _b !== void 0 ? _b : undefined;
            }
            var plugin = Plugin.getInstance({ editorId: editorId, editor: editor });
            plugin.editorService.onTransaction({ editor: editor, name: this.name });
        }
        catch (error) {
            // catchError('Error processing transaction', error);
        }
    },
});
export default TiptapVeltComments;
export function highlightComments(editor, commentAnnotations, editorId) {
    try {
        renderComments({ editor: editor, editorId: editorId, commentAnnotations: commentAnnotations });
    }
    catch (error) {
        VeltService.catchError('Error highlighting tiptap-velt-comments', error);
    }
}
export var renderComments = function (_a) {
    var _b, _c;
    var editor = _a.editor, editorId = _a.editorId, commentAnnotations = _a.commentAnnotations;
    try {
        if (editor) {
            if (!editorId) {
                editorId = (_b = EditorService.getEditorId(editor)) !== null && _b !== void 0 ? _b : undefined;
            }
            if (!Array.isArray(commentAnnotations)) {
                return;
            }
            commentAnnotations = JSON.parse(JSON.stringify(commentAnnotations));
            var tiptapCommentAnnotations = ((_c = commentAnnotations === null || commentAnnotations === void 0 ? void 0 : commentAnnotations.map(function (annotation) {
                var _a, _b;
                if (((_a = annotation === null || annotation === void 0 ? void 0 : annotation.context) === null || _a === void 0 ? void 0 : _a.tiptapConfig) && !((_b = annotation === null || annotation === void 0 ? void 0 : annotation.context) === null || _b === void 0 ? void 0 : _b.textEditorConfig)) {
                    annotation.context.textEditorConfig = annotation.context.tiptapConfig;
                    delete annotation.context.tiptapConfig;
                }
                return annotation;
            })) === null || _c === void 0 ? void 0 : _c.filter(function (annotation) {
                var _a, _b, _c;
                return !!((_a = annotation.context) === null || _a === void 0 ? void 0 : _a.textEditorConfig) &&
                    ((_c = (_b = annotation.context) === null || _b === void 0 ? void 0 : _b.textEditorConfig) === null || _c === void 0 ? void 0 : _c.editorId) === editorId;
            })) || [];
            var plugin = Plugin.getInstance({ editorId: editorId, editor: editor });
            plugin.storeService.updateGlobalStore({ editor: editor, comments: tiptapCommentAnnotations });
            VeltService.subscribeToSelectedAnnotationsMapSingleton();
        }
    }
    catch (error) {
        VeltService.catchError('Error highlighting tiptap-velt-comments', error);
    }
};
